import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { getPaymentStatus } from "@/lib/rexxponsApi";
import { useAuth } from "./AuthContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const started = useRef(false);
  const [state, setState] = useState("checking"); // checking | success | error

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("success");
          await refresh();
          toast.success("Payment confirmed — welcome to Rexxpons Pro!");
          setTimeout(() => navigate("/", { replace: true }), 1800);
          return;
        }
      } catch (e) {
        console.warn(`Payment status check ${attempts} failed, retrying`, e);
      }
      if (attempts >= MAX_POLLS) {
        setState("error");
        return;
      }
      setTimeout(poll, 1600);
    };
    poll();
  }, [navigate, refresh]);

  return (
    <div className="nexx-ambient min-h-screen bg-background text-foreground flex items-center justify-center px-4">
      <div className="text-center max-w-sm" data-testid="payment-success">
        {state === "checking" && (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Confirming your payment…</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 strokeWidth={1.5} className="w-12 h-12 text-primary mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">You're all set</h1>
            <p className="text-muted-foreground">Your Pro features are now unlocked. Redirecting…</p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle strokeWidth={1.5} className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">We couldn't confirm this yet</h1>
            <p className="text-muted-foreground mb-6">If you completed payment, it may take a moment. Your plan will update automatically.</p>
            <button
              data-testid="payment-home-btn"
              onClick={() => navigate("/", { replace: true })}
              className="rounded-full border border-border px-5 py-2.5 text-sm hover:bg-secondary transition-colors"
            >
              Back to Rexxpons
            </button>
          </>
        )}
      </div>
    </div>
  );
}
